/**
 * Consent Banner Component
 * 
 * Simple GDPR-compliant consent banner for EU users
 * Uses Google Consent Mode to manage ad consent
 */

import React, { useState, useEffect } from "react";

export const ConsentBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isEU, setIsEU] = useState(false);

  useEffect(() => {
    // Check if user is in EU (simplified check)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const euTimezones = [
      'Europe/Amsterdam', 'Europe/Berlin', 'Europe/Paris', 'Europe/Rome',
      'Europe/Madrid', 'Europe/Vienna', 'Europe/Brussels', 'Europe/Lisbon',
      'Europe/Dublin', 'Europe/Helsinki', 'Europe/Stockholm', 'Europe/Copenhagen',
      'Europe/Warsaw', 'Europe/Prague', 'Europe/Budapest', 'Europe/Bucharest',
      'Europe/Sofia', 'Europe/Zagreb', 'Europe/Ljubljana', 'Europe/Bratislava',
      'Europe/Tallinn', 'Europe/Riga', 'Europe/Vilnius', 'Europe/Athens',
      'Europe/Nicosia', 'Europe/Luxembourg', 'Europe/Malta', 'Europe/Vaduz',
      'Europe/Monaco', 'Europe/San_Marino', 'Europe/Vatican', 'Europe/Andorra',
      'Europe/London', 'Europe/Zurich', 'Europe/Geneva', 'Europe/Bern'
    ];
    
    const userInEU = euTimezones.some(tz => timezone.includes(tz.split('/')[1]));
    setIsEU(userInEU);

    // Check if consent was already given
    const consentGiven = localStorage.getItem('adConsent');
    if (userInEU && !consentGiven) {
      setShowBanner(true);
    } else if (!userInEU && consentGiven !== 'granted') {
      // Outside EU: grant consent by default and load AdSense
      localStorage.setItem('adConsent', 'granted');
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          'ad_storage': 'granted',
          'analytics_storage': 'granted',
          'functionality_storage': 'granted',
          'personalization_storage': 'granted'
        });
      }
      try {
        const existing = document.querySelector('script[data-adsbygoogle]');
        if (!existing) {
          const s = document.createElement('script');
          s.async = true;
          s.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4458181362254170';
          s.setAttribute('crossorigin', 'anonymous');
          s.setAttribute('data-adsbygoogle', 'true');
          document.head.appendChild(s);
        }
        if ((window as any).adsbygoogle) {
          ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
        }
      } catch {}
    }
  }, []);

  const acceptConsent = () => {
    // Update Google Consent Mode
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'ad_storage': 'granted',
        'analytics_storage': 'granted',
        'functionality_storage': 'granted',
        'personalization_storage': 'granted'
      });
    }
    
    // Store consent
    localStorage.setItem('adConsent', 'granted');
    setShowBanner(false);

    // Dynamically load AdSense script after consent
    try {
      const existing = document.querySelector('script[data-adsbygoogle]');
      if (!existing) {
        const s = document.createElement('script');
        s.async = true;
        s.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4458181362254170';
        s.setAttribute('crossorigin', 'anonymous');
        s.setAttribute('data-adsbygoogle', 'true');
        document.head.appendChild(s);
      }
      // Trigger any ads present
      if ((window as any).adsbygoogle) {
        ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
      }
    } catch {}
  };

  const denyConsent = () => {
    // Store denial
    localStorage.setItem('adConsent', 'denied');
    setShowBanner(false);
  };

  if (!showBanner || !isEU) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-slate-800 border-t border-slate-700 p-4 z-50">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-slate-300">
          <p className="mb-2">
            We use cookies and similar technologies to provide and improve our services, 
            including personalized ads. By continuing to use our site, you consent to our use of cookies.
          </p>
          <p className="text-xs text-slate-400">
            You can change your preferences at any time in your browser settings.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={denyConsent}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            Decline
          </button>
          <button
            onClick={acceptConsent}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};
